import { ImageType } from "@utilities/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageWithPreview } from "./interface";

type ControllerType = {
    images: ImageType[],
    onChange: (images: ImageType[], imageFiles: File[]) => void,
    baseImageUrl?: string
}

export const useController = ({ images, onChange, baseImageUrl }: ControllerType) => {

    const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const addNewImageRef = useRef<HTMLInputElement | null>(null);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [imagesWithPreviews, setImagesWithPreviews] = useState<ImageWithPreview[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);

    useEffect(()=> {
        if(images.length === 0){
            setImagesWithPreviews([]);
            setCurrentImageIndex(0);
            setImageFiles([]);
        }
    }, [images])

    useEffect(() => {
        // Construire une map rapide pour accéder aux previews existants
        const previewMap = new Map(
            imagesWithPreviews.map((img) => [`${img.fileDir}/${img.filename}`, img.preview])
        );

        const previews = images
            .filter((img) => img && img.filename)
            .map((img) => {
                const key = `${img.fileDir}/${img.filename}`;
                const preview = previewMap.get(key) || (img.fileDir && img.filename
                    ? `${baseImageUrl}/${img.fileDir}/${img.filename}`
                    : '');

                return {
                    ...img,
                    preview,
                };
            });

        setImagesWithPreviews(previews);

        // Corriger l'index courant si nécessaire
        if (currentImageIndex >= previews.length && previews.length > 0) {
            setCurrentImageIndex(previews.length - 1);
        }

    }, [images, baseImageUrl, currentImageIndex]);

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, index: number | null) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                setToastMessage('Veuillez sélectionner une image valide');
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            const tempFilename = file.name;

            const newImage: ImageType = {
                order: index !== null ? images[index]?.order || index : images.length,
                filename: tempFilename,
                fileDir: 'uploads',
            };

            const newImageWithPreview: ImageWithPreview = {
                ...newImage,
                preview: previewUrl,
            };

            let newImages: ImageType[];
            let newPreviews: ImageWithPreview[];
            let newFiles: File[];

            if (index !== null && index < images.length) {
                // Remplacement
                newImages = [...images];
                newPreviews = [...imagesWithPreviews];
                newFiles = [...imageFiles];
                newImages[index] = newImage;
                newPreviews[index] = newImageWithPreview;
                newFiles[index] = file;
            } else {
                // Ajout
                newImages = [...images, newImage];
                newPreviews = [...imagesWithPreviews, newImageWithPreview];
                newFiles = [...imageFiles, file];
                setCurrentImageIndex(newPreviews.length - 1);
            }

            setImagesWithPreviews(newPreviews);
            setImageFiles(newFiles);
            onChange(newImages, newFiles);

            e.target.value = '';
        },
        [images, imagesWithPreviews, imageFiles, onChange]
    );


    const removeImage = useCallback((index: number) => {
        if (index < 0 || index >= images.length) return;

        const previewToRemove = imagesWithPreviews[index]?.preview;
        if (previewToRemove && previewToRemove.startsWith('blob:')) {
            URL.revokeObjectURL(previewToRemove);
        }

        const newImages = images.filter((_, i) => i !== index);
        const reorderedImages = newImages.map((img, idx) => ({
            ...img,
            order: idx,
        }));

        const newPreviews = imagesWithPreviews.filter((_, i) => i !== index);
        const newFiles = imageFiles.filter((_, i) => i !== index);

        setImagesWithPreviews(newPreviews);
        setImageFiles(newFiles);

        if (currentImageIndex >= newPreviews.length && newPreviews.length > 0) {
            setCurrentImageIndex(Math.max(0, newPreviews.length - 1));
        } else if (newPreviews.length === 0) {
            setCurrentImageIndex(0);
        }

        onChange(reorderedImages, newFiles);
    }, [images, imagesWithPreviews, imageFiles, currentImageIndex, onChange]);


    const goToPreviousImage = useCallback(() => {
        if (imagesWithPreviews.length <= 1) return;

        setCurrentImageIndex(
            (prevIndex) =>
                prevIndex === 0 ? imagesWithPreviews.length - 1 : prevIndex - 1
        );
    }, [imagesWithPreviews.length]);

    const goToNextImage = useCallback(() => {
        if (imagesWithPreviews.length <= 1) return;

        setCurrentImageIndex(
            (prevIndex) => (prevIndex + 1) % imagesWithPreviews.length
        );
    }, [imagesWithPreviews.length]);

    const triggerFileInput = useCallback((index: number) => {
        fileInputRefs.current[index]?.click();
    }, []);

    const triggerAddNewImage = useCallback(() => {
        if (addNewImageRef.current) {
            addNewImageRef.current.click();
        } else {
            console.error("addNewImageRef is not set");
        }
    }, []);

    return {
        setToastMessage,
        setCurrentImageIndex,
        handleImageChange,
        removeImage,
        goToNextImage,
        goToPreviousImage,
        triggerFileInput,
        triggerAddNewImage,
        imageFiles,
        addNewImageRef,
        currentImageIndex,
        toastMessage,
        fileInputRefs,
        imagesWithPreviews,
    };
};
